export type PhoneSearchMode = "partial" | "full";

const MIN_PARTIAL_DIGITS = 2;
/** Israeli national number length without leading 0 (mobile 9, landline 8). */
const MIN_FULL_NATIONAL_DIGITS = 8;

/** Strips non-digits and removes Israeli country / leading-zero prefixes. */
export function normalizePhoneDigitsForSearch(raw: string): string {
  let digits = raw.replace(/\D/g, "");
  if (digits.startsWith("972")) {
    digits = digits.slice(3);
  } else if (digits.startsWith("0")) {
    digits = digits.slice(1);
  }
  return digits;
}

/** Converts user input to the canonical +972… format stored in the DB. */
export function normalizePhoneForFullSearch(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("972")) return `+${digits}`;
  if (digits.startsWith("0")) return `+972${digits.slice(1)}`;
  return `+972${digits}`;
}

export function buildPhoneNumberFieldFilter(
  raw: string,
  mode: PhoneSearchMode,
): { $contains: string } | undefined {
  const input = raw.replace(/\D/g, "");
  if (!input) return undefined;

  if (mode === "full") {
    const nationalDigits = normalizePhoneDigitsForSearch(input);
    if (nationalDigits.length < MIN_FULL_NATIONAL_DIGITS) return undefined;
    // hyperFilter supports $contains on relational fields; plain string equality does not work.
    return { $contains: normalizePhoneForFullSearch(input) };
  }

  const partial = normalizePhoneDigitsForSearch(input);
  if (partial.length < MIN_PARTIAL_DIGITS) return undefined;
  return { $contains: partial };
}

/** Restores display text for a phone filter from session meta. */
export function getPhoneFilterInputValue(storedValue: string | undefined): string {
  if (!storedValue) return "";
  if (storedValue.startsWith("+972")) {
    return "0" + storedValue.slice(4);
  }
  return storedValue.replace(/\D/g, "");
}

export function getPhoneSearchMode(
  storedMode: unknown,
): PhoneSearchMode {
  return storedMode === "full" ? "full" : "partial";
}
